import { useEffect, useMemo, useState } from "react";
import api from "../../../../util/api";
import { BrandApiType, ProductApiType } from "../../../../util/types";
import {
  OrderProductPriceType,
  ProductStatus,
  Policies,
  CurrencyCodes,
} from "@fena/toolkit-types";
import { usePolicyCheck } from "../../hooks/usePolicyCheck";

export interface IInfiniteScrollFilters {
  category?: string;
  brands?: string;
  status?: ProductStatus;
  searchKeyword?: string;
  onSale?: boolean;
  hasWholeSalePrice?: boolean;
  currency?: CurrencyCodes;
  location?: string;
}

export const useInfiniteScrollProductsList = (
  filters: IInfiniteScrollFilters = {}
) => {
  const { checkAccessByPolicies } = usePolicyCheck();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productsData, setProductsData] = useState<ProductApiType[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [statusFilter, setStatusFilter] = useState<ProductStatus>(
    ProductStatus.ACTIVE
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [priceType, setPriceType] = useState(false);

  const getProductList = useMemo(
    () => async () => {
      try {
        setLoading(true);
        const result = checkAccessByPolicies(
          [Policies.ORDER.ADMIN_ACCESS],
          [Policies.ORDER.CREATE_ORDERS]
        )
          ? await api.getOrderGenProducts(
              page,
              limit,
              statusFilter,
              categoryFilter,
              searchKeyword,
              brandFilter,
              priceType,
              {
                ...filters,
                sort: "createdAt",
                sortDirection: "DESC",
              }
            )
          : [];
        setHasNextPage(result.hasNextPage);
        if (page > 1) {
          setProductsData((prevProductsData) => [
            ...prevProductsData,
            ...result.docs,
          ]);
        } else {
          setProductsData(result.docs);
        }
        setLoading(false);
      } catch (e: any) {
        setLoading(false);
        setError(e);
      }
    },
    [
      page,
      statusFilter,
      categoryFilter,
      priceType,
      brandFilter,
      searchKeyword,
      filters.status,
      filters.category,
      filters.brands,
      filters.searchKeyword,
      filters.onSale,
      filters.hasWholeSalePrice,
      filters.currency,
      filters.location,
    ]
  );
  const onPaginationChange = (pageValue: number, limitValue: number) => {
    setPage(pageValue);
    setLimit(limitValue);
  };

  const onStatusChange = (status: ProductStatus) => {
    setStatusFilter(status);
    setPage(1);
  };

  const onCategoryChange = (category: string) => {
    setCategoryFilter(category);
    setPage(1);
  };

  const onSearchChange = (searchString: string) => {
    setSearchKeyword(searchString);
    setPage(1);
  };
  const onBrandFilterChange = (brand: BrandApiType[]) => {
    setBrandFilter(brand.map((brand) => brand._id).join(","));
    setPage(1);
  };

  const onPriceTypeFilterChange = (val: OrderProductPriceType) => {
    setPriceType(val === OrderProductPriceType.WHOLESALE);
    setPage(1);
  };

  useEffect(() => {
    getProductList();
  }, [getProductList, filters.location]);

  return {
    loading,
    getProductList,
    onStatusChange,
    onCategoryChange,
    onSearchChange,
    onPaginationChange,
    onBrandFilterChange,
    onPriceTypeFilterChange,
    productsData,
    error,
    hasNextPage,
  };
};
